/** Superadmin: accounts, reset links, and the zero-sum invariant check. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Spinner } from "../../components/ui/spinner";
import { ApiError, api } from "../../lib/api";
import { useSession } from "../auth/session";

interface AdminUser {
  id: number;
  email: string;
  name: string;
  is_superadmin: boolean;
  disabled_at: string | null;
}

interface Invariants {
  ok: boolean;
  checked_at: string;
  unbalanced: { group_id: number; total_base_cents: number }[];
}

export function SuperadminPage() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [link, setLink] = useState<{ url: string; expires: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newUser, setNewUser] = useState({ name: "", email: "" });

  const users = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.get<AdminUser[]>("/admin/users"),
    enabled: Boolean(user?.is_superadmin),
  });

  const invariants = useQuery({
    queryKey: ["invariants"],
    queryFn: () => api.get<Invariants>("/admin/health/invariants"),
    enabled: Boolean(user?.is_superadmin),
  });

  const resetLink = useMutation({
    mutationFn: (id: number) =>
      api.post<{ url: string; expires_at: string }>(`/admin/users/${id}/reset-link`),
    onSuccess: (result) => {
      setLink({ url: result.url, expires: result.expires_at });
      void navigator.clipboard?.writeText(result.url);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : "Failed."),
  });

  const toggleDisabled = useMutation({
    mutationFn: (input: { id: number; disabled: boolean }) =>
      api.patch(`/admin/users/${input.id}`, { disabled: input.disabled }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : "Failed."),
  });

  const create = useMutation({
    mutationFn: () => api.post("/admin/users", { name: newUser.name, email: newUser.email }),
    onSuccess: () => {
      setNewUser({ name: "", email: "" });
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : "Failed."),
  });

  if (!user?.is_superadmin) {
    return <p className="py-16 text-center text-sm text-muted">Superadmins only.</p>;
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-5 py-8">
      <div className="mb-6 flex items-baseline justify-between">
        <h1 className="display text-lg">Superadmin</h1>
        <Link to="/" className="text-sm text-accent underline underline-offset-2">
          Back
        </Link>
      </div>

      {error ? (
        <p role="alert" className="mb-3 rounded border border-owing/40 bg-owing/10 px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}

      <section className="mb-8">
        <h2 className="mb-2 text-2xs uppercase tracking-widest text-muted">Invariants</h2>
        {invariants.isPending ? (
          <Spinner />
        ) : invariants.data?.ok ? (
          <p className="rounded border border-owed/40 bg-owed/10 px-3 py-2 text-sm">
            Every group's balances sum to exactly zero.
          </p>
        ) : (
          <div className="rounded border border-owing/40 bg-owing/10 px-3 py-2 text-sm">
            <p className="font-medium">Some groups do not sum to zero:</p>
            <ul className="num mt-1">
              {invariants.data?.unbalanced.map((row) => (
                <li key={row.group_id}>
                  group {row.group_id}: {row.total_base_cents} cents
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-2xs uppercase tracking-widest text-muted">Accounts</h2>
        {link ? (
          <div className="mb-3 rounded border border-accent/40 bg-accent/5 px-3 py-2 text-sm">
            <p className="mb-1">Reset link copied to the clipboard. It works once.</p>
            <code className="block break-all text-xs">{link.url}</code>
          </div>
        ) : null}

        <ul className="divide-y divide-rule border-y border-rule">
          {(users.data ?? []).map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2 py-3">
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  {row.name}
                  {row.is_superadmin ? (
                    <span className="ml-2 rounded-full border border-rule px-1.5 text-2xs uppercase text-muted">
                      superadmin
                    </span>
                  ) : null}
                  {row.disabled_at ? (
                    <span className="ml-2 text-2xs uppercase text-owing">disabled</span>
                  ) : null}
                </span>
                <span className="text-xs text-muted">{row.email}</span>
              </span>
              <Button size="sm" onClick={() => resetLink.mutate(row.id)}>
                Reset link
              </Button>
              <Button
                size="sm"
                variant={row.disabled_at ? "secondary" : "danger"}
                onClick={() =>
                  toggleDisabled.mutate({ id: row.id, disabled: !row.disabled_at })
                }
              >
                {row.disabled_at ? "Enable" : "Disable"}
              </Button>
            </li>
          ))}
        </ul>

        <form
          className="mt-4 flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (newUser.name && newUser.email) create.mutate();
          }}
        >
          <Input
            className="flex-1"
            placeholder="Name"
            aria-label="New user name"
            value={newUser.name}
            onChange={(event) => setNewUser({ ...newUser, name: event.target.value })}
          />
          <Input
            className="flex-1"
            type="email"
            placeholder="Email"
            aria-label="New user email"
            value={newUser.email}
            onChange={(event) => setNewUser({ ...newUser, email: event.target.value })}
          />
          <Button type="submit" variant="primary">
            Create account
          </Button>
        </form>
        <p className="mt-2 text-xs text-muted">
          New accounts get an unusable password. Generate a reset link and hand it over.
        </p>
      </section>
    </main>
  );
}
